import { useApolloClient } from '@apollo/react-hooks';
import { CartIdContext } from '@jetshop/core/components/Cart/CartIdContext';
import { useContext, useState } from 'react';
import throwErrorInDev from '../helpers/throwErrorInDev';
import { useTracker } from '../analytics/Analytics';
import { trackProductEvent } from '../analytics/tracking';
import { unSlugify } from '../analytics/integrations/unslugify';
import { breadcrumbsWithoutProduct } from '../analytics/utils/breadcrumbsWithoutProduct';
export function useAddMultipleToCart(items, hookQueries, hookCallbacks) {
    const track = useTracker();
    const client = useApolloClient();
    const [status, setStatus] = useState('idle');
    const [data, setData] = useState();
    const { cartId, setCartId } = useContext(CartIdContext);
    if (!items) {
        throwErrorInDev(true, `You must pass an array of products to useAddMultipleToCart`);
        return null;
    }
    function updateCart({ data }) {
        const cartIdNotSet = !cartId;
        const cartIdFromMutation = data.addMultipleToCart.cart.id;
        const newCartIdReturnedFromAPI = cartIdFromMutation !== cartId;
        // Set the cart ID in context so any future add to cart actions use the same
        // ID
        if (cartIdNotSet || newCartIdReturnedFromAPI) {
            setCartId(cartIdFromMutation);
        }
    }
    function addMultiple(q, cbs) {
        const queries = hookQueries || q;
        const callbacks = hookCallbacks || cbs;
        if (!queries || !queries.addMultipleToCartMutation || !queries.cartQuery) {
            return Promise.reject(`You must pass cartQuery and addMultipleToCartMutation to useAddMultipletoCart or to the addMultiple fn`);
        }
        items.forEach((item) => {
            var _a, _b, _c;
            track(trackProductEvent({
                action: 'add',
                product: item,
                quantity: item.quantity,
                category: breadcrumbsWithoutProduct((_a = item.primaryRoute) === null || _a === void 0 ? void 0 : _a.breadcrumbs) ||
                    unSlugify((_c = (_b = item.canonicalCategory) === null || _b === void 0 ? void 0 : _b.primaryRoute) === null || _c === void 0 ? void 0 : _c.path) ||
                    ''
            }));
        });
        setStatus('loading');
        return client
            .mutate({
            mutation: queries.addMultipleToCartMutation,
            errorPolicy: 'all',
            variables: {
                cartId,
                items: items.map(({ articleNumber, quantity, comments, configurationIds, preOrderDate }) => ({
                    articleNumber,
                    quantity,
                    comments,
                    configurationIds,
                    preOrderDate
                }))
            },
            refetchQueries: (resp) => {
                // When errors are present, the cart does not get automatically
                // updated. We have to use refetchQueries in that case.
                return (resp.errors && [{ query: queries.cartQuery, variables: { cartId } }]);
            }
        })
            .then((resp) => {
            setData(resp);
            (callbacks === null || callbacks === void 0 ? void 0 : callbacks.onCompleted) && callbacks.onCompleted(resp);
            // Update the cart context
            updateCart(resp);
            if (resp.errors) {
                setStatus('partialFailure');
                (callbacks === null || callbacks === void 0 ? void 0 : callbacks.onPartialFailure) && callbacks.onPartialFailure(resp);
            }
            else {
                setStatus('success');
            }
            return resp;
        })
            .catch((err) => {
            console.log('error!', err);
            setStatus('error');
            (callbacks === null || callbacks === void 0 ? void 0 : callbacks.onError) && callbacks.onError(err);
            // TODO: We have to type cast the response until this is fixed in
            // react-apollo 3.2.0
            // https://github.com/apollographql/react-apollo/commit/a3dd7e64403de32e9048b2ebd5eb63e14d73cf26
        });
    }
    // If some of the items failed, create a Map containing those items as the key.
    // The errors returned from the API give us the index of failed items as they
    // appeared in the mutation's input, so we can use that to create the Map.
    // We also set the error code as the value
    const failedItems = new Map();
    if (status === 'partialFailure') {
        for (const error of data.errors) {
            const item = items[error.extensions.data.index];
            failedItems.set(item, error.extensions.code);
        }
    }
    // code: error.extensions.code
    // Maintain the same basic interface as `useMutation`.
    return [
        addMultiple,
        {
            loading: status === 'loading',
            called: status !== 'idle',
            error: status === 'error',
            data,
            failedItems
        }
    ];
}
//# sourceMappingURL=useAddMultipleToCart.js.map